// AuthForm.js
import React, { useState } from "react";
import { useForm } from 'react-hook-form';
import '../styles/Auth.css';
import {Button, Box, Typography, TextField, IconButton} from "@mui/material";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorIcon from '@mui/icons-material/Error';
import '../i18n';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import PropTypes from 'prop-types';


export default function AuthForm({ togglePasswordVisibility, passwordShown }) {
    const { t } = useTranslation();
    const { register, handleSubmit, formState: { errors }, clearErrors } = useForm({ mode: 'onChange' });
    const [authError, setAuthError] = useState(null);
    const userContext = useUser();

    const onSubmit = async (data) => {
        setAuthError(null);
        clearErrors();
        try {
            const params = new Map();
            params.set("login", data.login.trim())
                .set("password", data.password);

            const success = await userContext.login(params);
            if (!success) {
                setAuthError({
                    type: "INVALID_CREDENTIALS",
                    message: t("Неверный логин или пароль")
                });
            }
        } catch (error) {
            const invalidCredentials = error.response?.status === 401;
            setAuthError({
                type: invalidCredentials ? "INVALID_CREDENTIALS" : "SERVICE_UNAVAILABLE",
                message: invalidCredentials ? t("Неверный логин или пароль")
                    : "Не удалось связаться с сервером авторизации. Повторите попытку позже."
            });
        }
    };

    const title = t("Авторизация");
    const submitButtonText = t("Войти");
    const loginLabel = t("Логин");
    const passwordLabel = t("Пароль");

    const validateLogin = (value) => {
        if (!value || value.trim() === '') {
            return t("Обязательное поле");
        }
        const usernameRegex = /^[a-zA-Z0-9_-]+$/;
        if (!usernameRegex.test(value)) {
            return t("Логин может содержать только латинские буквы, цифры, дефис и подчеркивание");
        }
        return true;
    };

    const validatePassword = (value) => {
        if (!value) {
            return t("Обязательное поле");
        }
        return true;
    };

    const containerStyles = {
        bgcolor: 'background.paper',
        margin: 'auto',
        borderRadius: '15px',
        textAlign: 'center',
        p: 3,
        minWidth: '400px',
        width: '40%',
    };

    const titleStyles = {
        variant: "h5",
        mt: 2,
        mb: 3,
        color: 'text.primary'
    };

    const errorContainerStyles = {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        mt: 2,
        p: 1,
        borderRadius: 1,
        bgcolor: 'error.lighter'
    };

    return (
        <Box sx={containerStyles}>
            <Typography {...titleStyles}>
                {title}
            </Typography>

            <form className="Auth" onSubmit={handleSubmit(onSubmit)} noValidate>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={loginLabel}
                        className="AuthInput"
                        error={!!errors.login || !!authError}
                        helperText={errors.login?.message || (authError ? ' ' : '')}
                        {...register("login", {
                            validate: validateLogin
                        })}
                        onFocus={() => {
                            if (authError) {
                                setAuthError(null);
                            }
                        }}
                    />
                </Box>

                <Box className="passWrapper" sx={{ mb: 3, position: 'relative' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={passwordLabel}
                        className="AuthInput"
                        type={passwordShown ? "text" : "password"}
                        error={!!errors.password || !!authError}
                        helperText={errors.password?.message || (authError ? ' ' : '')}
                        {...register("password", {
                            validate: validatePassword
                        })}
                        onFocus={() => {
                            if (authError) {
                                setAuthError(null);
                            }
                        }}
                    />
                    <IconButton
                        className="password_icon"
                        onClick={togglePasswordVisibility}
                        color="info"
                        aria-label={passwordShown ? t("Скрыть пароль") : t("Показать пароль")}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }}
                    >
                        {passwordShown ? <VisibilityIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                </Box>

                {authError && (
                    <Box sx={errorContainerStyles}>
                        <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                        <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
                            {authError.message}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 3 }}>
                    <Button
                        className="confirmButton"
                        variant="contained"
                        type="submit"
                        fullWidth
                        size="large"
                        sx={{
                            minHeight: '48px',
                            position: 'relative'
                        }}
                    >
                        {submitButtonText}
                    </Button>
                </Box>
            </form>

            <Typography variant="body1" sx={{ my: 3, color: 'text.info' }}>
                {t("Для авторизации используйте логин и пароль от student-chat*.moevm.pro")}
            </Typography>
            
            <Typography variant="body1" sx={{ my: 3, color: 'text.secondary' }}>
                {t("В случае проблем с доступом обратитесь на support@moevm.pro")}
            </Typography>
        </Box>
    );
}

AuthForm.propTypes = {
    togglePasswordVisibility: PropTypes.func.isRequired,
    passwordShown: PropTypes.bool.isRequired
};
